import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { API, clearAdminToken, getAdminToken } from "@/components/admin/admin-utils";
import { invalidateMenuItemImage } from "@/components/admin/admin-utils";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { MenuItemsTab } from "@/components/admin/MenuItemsTab";
import { CategoriesTab } from "@/components/admin/CategoriesTab";
import { PromotionsTab } from "@/components/admin/PromotionsTab";
import { LocationsTab } from "@/components/admin/LocationsTab";
import { MenuMatrixTab } from "@/components/admin/MenuMatrixTab";
import { SmartFridgeTab } from "@/components/admin/SmartFridgeTab";
import { AdminOrdersTab } from "@/components/admin/AdminOrdersTab";
import { MenuItemModal } from "@/components/admin/MenuItemModal";
import { CategoryModal } from "@/components/admin/CategoryModal";
import { PromotionModal } from "@/components/admin/PromotionModal";
import { ItemLikesModal } from "@/components/admin/ItemLikesModal";

const EMPTY_MENU_FORM = {
  name: "",
  description: "",
  price: "",
  category: "",
  image_base64: null,
  is_active: true,
  variants: [],
  week_number: null,
  delivery_schedule: "all_week",
};

const EMPTY_CATEGORY_FORM = {
  key: "",
  label: "",
  description: "",
  color: "#1aa59a",
  icon: "restaurant",
  emoji: "",
  image_base64: null,
  is_rotating: false,
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getAdminToken());

  useEffect(() => {
    const onExpired = () => {
      setIsAuthenticated(false);
      // After the failing requests' own error toasts, replace them with one clear message
      setTimeout(() => toast.dismiss(), 0);
      setTimeout(
        () => toast.error("Your admin session has expired — please log in again", { id: "admin-session-expired" }),
        100
      );
    };
    window.addEventListener("admin-session-expired", onExpired);
    return () => window.removeEventListener("admin-session-expired", onExpired);
  }, []);

  const logout = () => {
    clearAdminToken();
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("menu");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);

  // Modals
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [likesModalOpen, setLikesModalOpen] = useState(false);
  const [likesItem, setLikesItem] = useState(null);

  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, item: null });

  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [menuForm, setMenuForm] = useState(EMPTY_MENU_FORM);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);
  const [promoForm, setPromoForm] = useState({
    title: "",
    subtitle: "",
    link_url: "",
    image_base64: null,
    is_active: false,
    is_public: true,
    available_locations: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [menuRes, catRes, promoRes, locRes] = await Promise.all([
        axios.get(`${API}/menu?include_inactive=true`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/promotions`),
        axios.get(`${API}/admin/locations`),
      ]);
      setMenuItems(menuRes.data);
      setCategories(catRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setPromotions(promoRes.data);
      setLocationsList(locRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  // ---- Menu Item handlers ----
  const openMenuModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setMenuForm({
        name: item.name,
        description: item.description,
        price: item.price.toString(),
        category: item.category,
        image_base64: item.image_base64,
        is_active: item.is_active !== false,
        variants: item.variants || [],
        week_number: item.week_number || null,
        delivery_schedule: item.delivery_schedule || "all_week",
      });
    } else {
      const defaultCategory = selectedCategoryFilter || categories[0]?.key || "";
      const isRotating = categories.find((c) => c.key === defaultCategory)?.is_rotating;
      setEditingItem(null);
      setMenuForm({
        ...EMPTY_MENU_FORM,
        category: defaultCategory,
        week_number: isRotating ? 1 : null,
      });
    }
    setMenuModalOpen(true);
  };

  const saveMenuItem = async () => {
    if (!menuForm.name || !menuForm.price || !menuForm.category) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...menuForm,
        price: parseFloat(menuForm.price),
        variants: menuForm.variants.map((v) => ({ name: v.name, price: parseFloat(v.price) || 0 })),
      };

      if (editingItem) {
        await axios.put(`${API}/menu/${editingItem.id}`, data);
        // Force every mounted image component to drop its cached copy and
        // refetch — the newly-saved picture now shows without a page reload.
        invalidateMenuItemImage(editingItem.id);
        toast.success("Menu item updated");
      } else {
        const res = await axios.post(`${API}/menu`, data);
        // Fresh item may have an image already; invalidate to be safe.
        if (res?.data?.id) invalidateMenuItemImage(res.data.id);
        toast.success("Menu item created");
      }
      setMenuModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save menu item");
    } finally {
      setSaving(false);
    }
  };

  const moveItem = async (item, direction) => {
    const categoryKey = selectedCategoryFilter || item.category;
    const categoryItems = menuItems
      .filter((i) => i.category === categoryKey)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    const currentIndex = categoryItems.findIndex((i) => i.id === item.id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= categoryItems.length) return;

    const newOrder = [...categoryItems];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

    setMenuItems(
      menuItems.map((mi) => {
        const orderIndex = newOrder.findIndex((no) => no.id === mi.id);
        return orderIndex >= 0 ? { ...mi, display_order: orderIndex } : mi;
      })
    );

    setReordering(true);
    try {
      await axios.post(`${API}/menu/reorder/${categoryKey}`, { item_ids: newOrder.map((i) => i.id) });
    } catch {
      toast.error("Failed to save order");
      fetchData();
    } finally {
      setReordering(false);
    }
  };

  // ---- Category handlers ----
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        key: category.key,
        label: category.label,
        description: category.description || "",
        color: category.color || "#1aa59a",
        icon: category.icon || "restaurant",
        emoji: category.emoji || "",
        image_base64: category.image_base64,
        is_rotating: category.is_rotating || false,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm(EMPTY_CATEGORY_FORM);
    }
    setCategoryModalOpen(true);
  };

  const moveCategoryItem = async (category, direction) => {
    const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedCategories.findIndex((c) => c.id === category.id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= sortedCategories.length) return;

    const newOrder = [...sortedCategories];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

    setCategories(newOrder.map((cat, idx) => ({ ...cat, order: idx })));

    setReordering(true);
    try {
      await axios.put(`${API}/admin/categories/reorder`, { category_ids: newOrder.map((c) => c.id) });
    } catch {
      toast.error("Failed to save category order");
      fetchData();
    } finally {
      setReordering(false);
    }
  };

  const saveCategory = async () => {
    if (!categoryForm.key || !categoryForm.label) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm);
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, categoryForm);
        toast.success("Category created");
      }
      setCategoryModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  // ---- Promotion handlers ----
  const openPromoModal = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      title: promo.title || "",
      subtitle: promo.subtitle || "",
      link_url: promo.link_url || "",
      image_base64: promo.image_base64,
      is_active: promo.is_active,
      is_public: promo.is_public !== false,
      available_locations: promo.available_locations || [],
    });
    setPromoModalOpen(true);
  };

  const savePromotion = async () => {
    setSaving(true);
    try {
      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, promoForm);
        toast.success("Promotion updated");
      } else {
        await axios.post(`${API}/promotions`, { ...promoForm, slot: 0 });
        toast.success("Promotion created");
      }
      setPromoModalOpen(false);
      fetchData();
    } catch {
      toast.error("Failed to save promotion");
    } finally {
      setSaving(false);
    }
  };

  const openCreatePromoModal = () => {
    setEditingPromo(null);
    setPromoForm({
      title: "",
      subtitle: "",
      link_url: "",
      image_base64: null,
      is_active: false,
      is_public: true,
      available_locations: [],
    });
    setPromoModalOpen(true);
  };

  // ---- Delete handlers ----
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget.type === "menu") {
        await axios.delete(`${API}/menu/${deleteTarget.item.id}`);
        toast.success("Menu item deleted");
      } else if (deleteTarget.type === "category") {
        await axios.delete(`${API}/categories/${deleteTarget.item.id}`);
        toast.success("Category deleted");
      } else if (deleteTarget.type === "promo") {
        await axios.delete(`${API}/promotions/${deleteTarget.item.id}`);
        toast.success("Promotion deleted");
      }
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Filtered/sorted menu items
  const filteredMenuItems = (selectedCategoryFilter
    ? menuItems.filter((item) => item.category === selectedCategoryFilter)
    : menuItems
  ).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  if (!isAuthenticated) {
    return <AdminLogin onAuthSuccess={() => setIsAuthenticated(true)} onBack={() => navigate("/")} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <AdminHeader onBack={() => navigate("/")} reordering={reordering} onLogout={logout} />

      <main
        className={`mx-auto p-4 ${
          ["matrix", "orders"].includes(activeTab)
            ? "max-w-none xl:px-8"
            : "max-w-3xl"
        }`}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col gap-3 mb-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="menu" data-testid="tab-menu" className="text-xs sm:text-sm">
                Menu Items
              </TabsTrigger>
              <TabsTrigger value="categories" data-testid="tab-categories" className="text-xs sm:text-sm">
                Categories
              </TabsTrigger>
              <TabsTrigger value="promotions" data-testid="tab-promotions" className="text-xs sm:text-sm">
                Promotions
              </TabsTrigger>
              <TabsTrigger value="locations" data-testid="tab-locations" className="text-xs sm:text-sm">
                Locations
              </TabsTrigger>
              <TabsTrigger value="matrix" data-testid="tab-matrix" className="text-xs sm:text-sm">
                Availability & Pricing
              </TabsTrigger>
              <TabsTrigger value="smart-fridge" data-testid="tab-smart-fridge" className="text-xs sm:text-sm">
                Smart Fridge
              </TabsTrigger>
              <TabsTrigger value="orders" data-testid="tab-orders" className="text-xs sm:text-sm">
                Orders
              </TabsTrigger>
            </TabsList>

            {(activeTab === "menu" || activeTab === "categories") && (
              <Button
                onClick={() => (activeTab === "menu" ? openMenuModal() : openCategoryModal())}
                className="bg-[#1aa59a] hover:bg-[#158a80] w-full"
                data-testid="add-new-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add {activeTab === "menu" ? "Item" : "Category"}
              </Button>
            )}
          </div>

          <TabsContent value="menu">
            <MenuItemsTab
              loading={loading}
              items={filteredMenuItems}
              categories={categories}
              selectedCategoryFilter={selectedCategoryFilter}
              onSelectCategoryFilter={setSelectedCategoryFilter}
              reordering={reordering}
              onMoveItem={moveItem}
              onEditItem={openMenuModal}
              onDeleteItem={(item) => confirmDelete("menu", item)}
              onViewLikes={(item) => {
                setLikesItem(item);
                setLikesModalOpen(true);
              }}
              onBulkUpdated={fetchData}
            />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesTab
              loading={loading}
              categories={categories}
              reordering={reordering}
              onMoveCategory={moveCategoryItem}
              onEditCategory={openCategoryModal}
              onDeleteCategory={(cat) => confirmDelete("category", cat)}
            />
          </TabsContent>

          <TabsContent value="promotions">
            <PromotionsTab
              loading={loading}
              promotions={promotions}
              locations={locationsList}
              onEditPromotion={openPromoModal}
              onCreatePromotion={openCreatePromoModal}
              onDeletePromotion={(promo) => confirmDelete("promo", promo)}
            />
          </TabsContent>

          <TabsContent value="locations">
            <LocationsTab />
          </TabsContent>

          <TabsContent value="matrix">
            <MenuMatrixTab />
          </TabsContent>

          <TabsContent value="smart-fridge">
            <SmartFridgeTab />
          </TabsContent>

          <TabsContent value="orders">
            <AdminOrdersTab />
          </TabsContent>
        </Tabs>
      </main>

      <MenuItemModal
        open={menuModalOpen}
        onOpenChange={setMenuModalOpen}
        editingItem={editingItem}
        menuForm={menuForm}
        setMenuForm={setMenuForm}
        categories={categories}
        onSave={saveMenuItem}
        saving={saving}
      />

      <CategoryModal
        open={categoryModalOpen}
        onOpenChange={setCategoryModalOpen}
        editingCategory={editingCategory}
        categoryForm={categoryForm}
        setCategoryForm={setCategoryForm}
        onSave={saveCategory}
        saving={saving}
      />

      <PromotionModal
        open={promoModalOpen}
        onOpenChange={setPromoModalOpen}
        editingPromo={editingPromo}
        promoForm={promoForm}
        setPromoForm={setPromoForm}
        onSave={savePromotion}
        saving={saving}
        locations={locationsList}
      />

      <ItemLikesModal
        open={likesModalOpen}
        onOpenChange={setLikesModalOpen}
        item={likesItem}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget.type === "category"
                ? "This will delete the category. Make sure no menu items are using this category."
                : deleteTarget.type === "promo"
                ? "This will permanently delete this promotion across all locations."
                : "This will permanently delete this item."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
